/**
 * AttributeFormPanel — Create / Edit attributes in the Attribute Library tab.
 *
 * Modes:
 *   Create mode (selectedAttr = null): basic form fields only
 *   Edit mode   (selectedAttr = {...}): pre-filled form + Industry Label Overrides + Country Mandatory
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Save, Info, Globe2, Tag, ChevronDown, ChevronRight, X, Settings, Trash2, GripVertical } from 'lucide-react';
import { erpToast } from '../../components/erp/ERPToast';
import { API_BASE as API_URL, getToken } from '../../lib/api';

const hdrs = () => ({ Authorization: `Bearer ${getToken() || ''}`, 'Content-Type': 'application/json' });

export const CONTROL_TYPE_GROUPS = {
  Basic: ['TextEdit', 'ComboBoxEdit', 'LookUpEdit', 'DateEdit', 'CheckEdit', 'RadioGroup', 'CalcEdit'],
  Advanced: ['MemoEdit', 'LinkEdit', 'ApiLookupEdit', 'GridEdit'],
  Media: ['ImageEdit', 'FileAttachment'],
};
const DROPDOWN_CONTROLS = new Set(['LookUpEdit', 'ComboBoxEdit', 'RadioGroup', 'ApiLookupEdit']);
const TEXT_CONTROLS = new Set(['TextEdit', 'MemoEdit', 'CalcEdit', 'SpinEdit']);
const VALIDATION_RULES = [
  { id: 'none', label: 'None' }, { id: 'numeric', label: 'Numbers only' },
  { id: 'decimal', label: 'Decimal allowed' }, { id: 'alpha', label: 'Letters only' },
  { id: 'alphanumeric', label: 'Letters & numbers' }, { id: 'phone', label: 'Phone number' },
  { id: 'email', label: 'Email address' },
];
const SOURCE_TABLES = [
  // Item Master
  'ItemMaster',
  // Classification masters
  'DivisionMaster', 'Sec_DepMaster', 'CategoryMaster', 'Sub_catMaster',
  // Style/variant masters
  'BrandMaster', 'StyleMaster', 'DesignMaster', 'SizeMaster', 'ColorMaster',
  // Unit & article
  'UOMMaster', 'Article_Master',
  // Finance & branch
  'M_GeneralLedger', 'BranchMaster',
  // Price
  'Price_Master',
];
const PRICE_MASTER_COLUMNS = ['mrp', 'P_rate', 'P_wsp_rate', 'S_wsp_Price', 'dis_type', 'dis_value'];
export const GROUP_LEGEND = {
  grp_classification: 'Where the item sits in the merchandise hierarchy — Division, Department, Category, Sub-Category. Drives reporting rollups.',
  grp_style: 'What makes one item different from another of the same category — Brand, Style, Design, Colour, Size. Drives variant/SKU generation.',
  grp_physical: 'Physical construction details specific to apparel/soft-goods — Fabric, Fit, Collar, Wash. Usually industry-specific.',
  grp_compliance: 'Regulatory / reference fields needed for invoicing & traceability — HSN, Batch, Expiry.',
  grp_pricing: 'Anything that affects the selling price — MRP, cost, wholesale rate, discount. Writes to Price_Master.',
};

const EMPTY_FORM = {
  AttributeId: '', Label: '', ControlType: 'TextEdit', GroupId: '',
  SourceTable: '', ItemMasterColumn: '', PriceMasterColumn: '',
  ValidationRule: 'none', MaxLength: '',
  dataSource: 'masterTable', sqlType: 'query', sqlText: '',
  globalizationSource: 'countries', industryForStatic: '', industryStaticOptions: '', industryOptionsJson: '{}',
  // Media/Image params
  imageAccept: 'image/*', imageMaxKb: '500', imageThumbnail: true,
  fileAccept: '*', fileMaxKb: '2048',
};

// ─── GetInfo Preview Modal ────────────────────────────────────────────────────
export { GetInfoPreviewModal };

function GetInfoPreviewModal({ tableName, attrId, label, onClose }) {
  const controlName = `AE_${attrId}`;
  const [query, setQuery] = useState('');
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchField, setSearchField] = useState('');
  const [searchOp, setSearchOp] = useState('Like');
  const [searchVal, setSearchVal] = useState('');
  const [total, setTotal] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);

  // Load config on open
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/api/attribute-engine/getinfo/${encodeURIComponent(tableName)}/${encodeURIComponent(controlName)}`, { headers: hdrs() });
        const j = await r.json();
        const cfg = j.rows || [];
        if (cfg.length) {
          setQuery(cfg[0].Query || '');
          setColumns(cfg.map(c => ({ col: c.ColumnName, alias: c.AliasName, value: !!c.ValueMember, display: !!c.DisplayMember, searchable: !!c.SetDefault })));
          const defSearch = cfg.find(c => c.SetDefault);
          setSearchField(defSearch?.AliasName || cfg[0].AliasName);
        } else {
          setQuery(`SELECT * FROM dbo.${tableName} ORDER BY 1`);
        }
      } catch { erpToast.error('Failed to load config'); }
      setLoading(false);
    })();
  }, [tableName, controlName]);

  const runQuery = async (q) => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/attribute-engine/getinfo/preview`, {
        method: 'POST', headers: hdrs(), body: JSON.stringify({ query: q }),
      });
      const j = await r.json();
      if (j.success) { setRows(j.rows); setTotal(j.total); }
      else erpToast.error(j.error);
    } catch { erpToast.error('Network error'); }
    setLoading(false);
    setInitialLoad(false);
  };

  const handleSearch = () => {
    let q = query;
    if (searchVal.trim() && columns.length) {
      const col = columns.find(c => c.alias === searchField)?.col || columns[0]?.col || '';
      if (col) {
        const op = searchOp === 'Like' ? `LIKE '%${searchVal}%'` : `= '${searchVal}'`;
        q = query.replace(/\s+ORDER\s+BY.*/i, '') + ` AND ${col} ${op}`;
      }
    }
    runQuery(q);
  };

  // Derive visible columns from rows if no config
  const gridCols = columns.length > 0
    ? columns
    : rows.length > 0 ? Object.keys(rows[0]).map(k => ({ col: k, alias: k })) : [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 8, width: 720, maxWidth: '96vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #e2e8f0', background: '#1e40af', borderRadius: '8px 8px 0 0' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', flex: 1 }}>GetInfo Preview — {label || tableName}</span>
          <span style={{ fontSize: 10, color: '#bfdbfe', marginRight: 12 }}>{controlName}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 2 }}><X size={16} /></button>
        </div>

        {/* SQL Editor strip */}
        <div style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: '#475569', marginBottom: 4 }}>SQL Query (editable)</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <textarea value={query} onChange={e => setQuery(e.target.value)} rows={3} spellCheck={false}
              style={{ flex: 1, fontFamily: 'Consolas,monospace', fontSize: 11, padding: '6px 8px', border: '1.5px solid #cbd5e1', borderRadius: 4, resize: 'none', outline: 'none', background: '#fff', color: '#0f172a' }} />
            <button onClick={() => runQuery(query)}
              style={{ padding: '0 14px', borderRadius: 4, border: 'none', background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Run ▶
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #e2e8f0', background: '#f1f5f9' }}>
          <select value={searchField} onChange={e => setSearchField(e.target.value)}
            style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11, outline: 'none' }}>
            {gridCols.map(c => <option key={c.col} value={c.alias}>{c.alias}</option>)}
          </select>
          <select value={searchOp} onChange={e => setSearchOp(e.target.value)}
            style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11, outline: 'none', width: 90 }}>
            <option value="Like">Like %…%</option>
            <option value="=">Equals =</option>
            <option value="StartsWith">Starts with</option>
          </select>
          <input value={searchVal} onChange={e => setSearchVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Search…"
            style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11, outline: 'none' }} />
          <button onClick={handleSearch}
            style={{ padding: '4px 14px', borderRadius: 4, border: 'none', background: '#0f172a', color: '#fff', fontSize: 11, cursor: 'pointer' }}>
            Search
          </button>
          {total > 0 && <span style={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap' }}>{total} rows</span>}
        </div>

        {/* Grid */}
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: '#64748b' }}>Loading…</div>
          ) : initialLoad ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: '#94a3b8' }}>Click <b>Run ▶</b> or <b>Search</b> to preview data</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: '#94a3b8' }}>No rows returned</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                  {gridCols.map(c => (
                    <th key={c.col} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1.5px solid #e2e8f0', whiteSpace: 'nowrap', fontSize: 10 }}>
                      {c.alias}
                      {c.value && <span style={{ marginLeft: 4, fontSize: 8, background: '#dbeafe', color: '#1d4ed8', padding: '1px 4px', borderRadius: 8 }}>ID</span>}
                      {c.display && <span style={{ marginLeft: 4, fontSize: 8, background: '#dcfce7', color: '#166534', padding: '1px 4px', borderRadius: 8 }}>Display</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    {gridCols.map(c => (
                      <td key={c.col} style={{ padding: '5px 10px', color: '#334155', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row[c.col] ?? row[c.alias] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


// Shown when ControlType=LookUpEdit and SourceTable is set.
// Reads/writes WebApp.SearchInfoConfig in RC26MASTERDB using ControlName=AE_{attrId}.
function GetInfoConfigPanel({ tableName, attrId, label }) {
  const controlName = `AE_${attrId}`;
  const [open, setOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [query, setQuery] = useState('');
  const [columns, setColumns] = useState([]); // [{ColumnName, AliasName, ValueMember, DisplayMember, SetDefault, OrderBy, IsActive}]
  const [existingControls, setExistingControls] = useState([]);
  const [statusColumn, setStatusColumn] = useState('');
  const [whereCondition, setWhereCondition] = useState('');
  const [multiSelect, setMultiSelect] = useState(false);
  const [autoLoad, setAutoLoad] = useState(false);
  const [predictive, setPredictive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (ctrl) => {
    if (!tableName) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/attribute-engine/getinfo/${encodeURIComponent(tableName)}/${encodeURIComponent(ctrl || controlName)}`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) {
        setExistingControls(j.controls || []);
        const rows = j.rows || [];
        if (rows.length > 0) {
          setQuery(rows[0].Query || `SELECT * FROM dbo.${tableName}`);
          setStatusColumn(rows[0].StatusColumn || '');
          setWhereCondition(rows[0].WhereCondition || '');
          setColumns(rows.map(r => ({
            ColumnName: r.ColumnName, AliasName: r.AliasName,
            ValueMember: !!r.ValueMember, DisplayMember: !!r.DisplayMember,
            SetDefault: !!r.SetDefault, OrderBy: !!r.OrderBy,
          })));
        } else {
          // New config — default template
          setQuery(`SELECT * FROM dbo.${tableName} ORDER BY 1`);
          setColumns([
            { ColumnName: 'Id', AliasName: 'ID', ValueMember: true, DisplayMember: false, SetDefault: false, OrderBy: true },
            { ColumnName: 'Name', AliasName: 'Name', ValueMember: false, DisplayMember: true, SetDefault: true, OrderBy: false },
          ]);
        }
      }
    } catch { erpToast.error('Failed to load GetInfo config'); }
    setLoading(false);
  }, [tableName, controlName]);

  useEffect(() => { if (open) load(controlName); }, [open, load, controlName]);

  const addCol = () => setColumns(prev => [...prev, { ColumnName: '', AliasName: '', ValueMember: false, DisplayMember: false, SetDefault: false, OrderBy: false }]);
  const removeCol = (i) => setColumns(prev => prev.filter((_, idx) => idx !== i));
  const updateCol = (i, key, val) => setColumns(prev => prev.map((c, idx) => idx === i ? { ...c, [key]: val } : c));

  const save = async () => {
    if (!query.trim()) { erpToast.error('SQL query is required'); return; }
    if (!columns.length) { erpToast.error('Add at least one column'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/attribute-engine/getinfo/${encodeURIComponent(tableName)}/${encodeURIComponent(controlName)}`, {
        method: 'PUT', headers: hdrs(),
        body: JSON.stringify({ columns, query, statusColumn, whereCondition, multiSelect, autoLoad, predictive }),
      });
      const j = await r.json();
      if (j.success) erpToast.success(`GetInfo config saved — ${j.saved} column${j.saved !== 1 ? 's' : ''}`);
      else erpToast.error(j.error || 'Save failed');
    } catch { erpToast.error('Network error'); }
    setSaving(false);
  };

  if (!tableName) return null;
  return (
    <div style={{ marginTop: 10, border: '1.5px solid #3b82f6', borderRadius: 6, overflow: 'hidden' }}>
      {/* Toggle header */}
      <div
        onClick={() => setOpen(p => !p)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', background: '#eff6ff', cursor: 'pointer', userSelect: 'none' }}
      >
        <Settings size={13} color="#2563eb" />
        <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8' }}>Configure GetInfo — {tableName}</span>
        <span style={{ fontSize: 9, color: '#64748b', marginLeft: 4 }}>Control: {controlName}</span>
        <button onClick={e => { e.stopPropagation(); setShowPreview(true); }}
          data-testid="getinfo-preview-btn"
          style={{ marginLeft: 'auto', padding: '2px 10px', borderRadius: 4, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
          Preview
        </button>
        <span style={{ color: '#2563eb' }}>{open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</span>
      </div>

      {showPreview && <GetInfoPreviewModal tableName={tableName} attrId={attrId} label={label} onClose={() => setShowPreview(false)} />}

      {open && (
        <div style={{ padding: 12, background: '#fff', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading ? <div style={{ fontSize: 11, color: '#64748b' }}>Loading…</div> : (<>

            {/* Load existing control */}
            {existingControls.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: '#64748b' }}>
                <span>Load from existing:</span>
                {existingControls.map(ec => (
                  <button key={ec.ControlName} onClick={() => load(ec.ControlName)}
                    style={{ fontSize: 9, padding: '1px 7px', borderRadius: 10, border: '1px solid #cbd5e1', background: '#f1f5f9', cursor: 'pointer', color: '#334155' }}>
                    {ec.ControlName} ({ec.ColCount} cols)
                  </button>
                ))}
              </div>
            )}

            {/* SQL Editor */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#475569', marginBottom: 4 }}>SQL Query</div>
              <textarea
                value={query} onChange={e => setQuery(e.target.value)}
                rows={5}
                spellCheck={false}
                data-testid="getinfo-sql-editor"
                style={{ width: '100%', fontFamily: 'Consolas, monospace', fontSize: 11, padding: '7px 8px', borderRadius: 4, border: '1.5px solid #cbd5e1', resize: 'vertical', outline: 'none', background: '#f8fafc', color: '#0f172a', lineHeight: 1.5, boxSizing: 'border-box' }}
              />
            </div>

            {/* Columns grid */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: '#475569' }}>Grid Columns</span>
                <button onClick={addCol}
                  style={{ fontSize: 9, padding: '1px 8px', borderRadius: 3, border: '1px solid #2563eb', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer' }}>
                  + Add Column
                </button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    {['Column Name', 'Alias / Header', 'Value', 'Display', 'Default Search', 'Order By', ''].map(h => (
                      <th key={h} style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {columns.map((col, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '3px 4px' }}>
                        <input value={col.ColumnName} onChange={e => updateCol(i, 'ColumnName', e.target.value)}
                          placeholder="e.g. DivUId"
                          style={{ width: '100%', padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 3, fontSize: 10, fontFamily: 'monospace', outline: 'none' }} />
                      </td>
                      <td style={{ padding: '3px 4px' }}>
                        <input value={col.AliasName} onChange={e => updateCol(i, 'AliasName', e.target.value)}
                          placeholder="e.g. Div Id"
                          style={{ width: '100%', padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 3, fontSize: 10, outline: 'none' }} />
                      </td>
                      {['ValueMember', 'DisplayMember', 'SetDefault', 'OrderBy'].map(key => (
                        <td key={key} style={{ padding: '3px 4px', textAlign: 'center' }}>
                          <input type="checkbox" checked={!!col[key]} onChange={e => updateCol(i, key, e.target.checked)} style={{ width: 14, height: 14, cursor: 'pointer' }} />
                        </td>
                      ))}
                      <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                        <button onClick={() => removeCol(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 1 }}>
                          <Trash2 size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Options row */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', paddingTop: 4, borderTop: '1px dashed #e2e8f0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={autoLoad} onChange={e => setAutoLoad(e.target.checked)} style={{ width: 13, height: 13 }} />
                Auto Load on Open
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={predictive} onChange={e => setPredictive(e.target.checked)} style={{ width: 13, height: 13 }} />
                Predictive Search
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={multiSelect} onChange={e => setMultiSelect(e.target.checked)} style={{ width: 13, height: 13 }} />
                Multi-Select
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 10, color: '#64748b' }}>Status Column:</span>
                <input value={statusColumn} onChange={e => setStatusColumn(e.target.value)}
                  placeholder="e.g. Status"
                  style={{ width: 80, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 3, fontSize: 10, outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 10, color: '#64748b' }}>WHERE extra:</span>
                <input value={whereCondition} onChange={e => setWhereCondition(e.target.value)}
                  placeholder="e.g. IsActive=1"
                  style={{ width: 120, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 3, fontSize: 10, outline: 'none' }} />
              </div>
            </div>

            {/* Save button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
              <button onClick={save} disabled={saving} data-testid="getinfo-save-btn"
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 16px', borderRadius: 4, border: 'none', background: saving ? '#94a3b8' : '#2563eb', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                <Save size={12} /> {saving ? 'Saving…' : 'Save GetInfo Config'}
              </button>
            </div>
          </>)}
        </div>
      )}
    </div>
  );
}


const COMMON_COUNTRIES = [
  { code: 'IND', name: 'India', flag: '🇮🇳' }, { code: 'ARE', name: 'UAE', flag: '🇦🇪' },
  { code: 'SAU', name: 'Saudi Arabia', flag: '🇸🇦' }, { code: 'QAT', name: 'Qatar', flag: '🇶🇦' },
  { code: 'KWT', name: 'Kuwait', flag: '🇰🇼' }, { code: 'BHR', name: 'Bahrain', flag: '🇧🇭' },
  { code: 'OMN', name: 'Oman', flag: '🇴🇲' }, { code: 'USA', name: 'United States', flag: '🇺🇸' },
  { code: 'GBR', name: 'United Kingdom', flag: '🇬🇧' }, { code: 'SGP', name: 'Singapore', flag: '🇸🇬' },
  { code: 'AUS', name: 'Australia', flag: '🇦🇺' }, { code: 'MYS', name: 'Malaysia', flag: '🇲🇾' },
  { code: 'PAK', name: 'Pakistan', flag: '🇵🇰' }, { code: 'BGD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: 'LKA', name: 'Sri Lanka', flag: '🇱🇰' }, { code: 'NPL', name: 'Nepal', flag: '🇳🇵' },
  { code: 'ZAF', name: 'South Africa', flag: '🇿🇦' }, { code: 'CAN', name: 'Canada', flag: '🇨🇦' },
  { code: 'DEU', name: 'Germany', flag: '🇩🇪' }, { code: 'FRA', name: 'France', flag: '🇫🇷' },
];

export const AttributeFormPanel = ({ groups, industries = [], onSaved, selectedAttr = null, hideIndustrySection = false }) => {
  const isEditMode = !!selectedAttr;
  const [form, setForm] = useState(EMPTY_FORM);
  const [target, setTarget] = useState('lookup');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  // Industry label overrides (edit mode only)
  const [industryMappings, setIndustryMappings] = useState([]);
  const [labelEdits, setLabelEdits] = useState({});
  const [savingLabels, setSavingLabels] = useState(false);
  const [showIndustryLabels, setShowIndustryLabels] = useState(false);

  // Country mandatory (edit mode only)
  const [mandatoryCodes, setMandatoryCodes] = useState([]);
  const [savingMandatory, setSavingMandatory] = useState(false);
  const [showCountryMandatory, setShowCountryMandatory] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  // Auto-detected company country
  const [companyCountry, setCompanyCountry] = useState(null);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  // Auto-set 'custom' target for media controls
  useEffect(() => {
    if (['ImageEdit', 'FileAttachment'].includes(form.ControlType)) {
      setTarget('custom');
    }
  }, [form.ControlType]);

  // Load company country code once
  useEffect(() => {
    fetch(`${API_URL}/api/attribute-engine/region`, { headers: hdrs() })
      .then(r => r.json())
      .then(j => { if (j.success && j.data?.CountryCode) setCompanyCountry(j.data.CountryCode); })
      .catch(() => {});
  }, []);

  // Pre-fill form and load extra data when selectedAttr changes
  useEffect(() => {
    if (!selectedAttr) {
      setForm(EMPTY_FORM);
      setTarget('lookup');
      setIndustryMappings([]);
      setLabelEdits({});
      setMandatoryCodes([]);
      return;
    }
    // Pre-fill basic form fields
    setForm({
      AttributeId: selectedAttr.AttributeId || '',
      Label: selectedAttr.Label || '',
      ControlType: selectedAttr.ControlType || 'TextEdit',
      GroupId: selectedAttr.GroupId || '',
      SourceTable: selectedAttr.SourceTable || '',
      ItemMasterColumn: selectedAttr.ItemMasterColumn || '',
      PriceMasterColumn: selectedAttr.PriceMasterColumn || '',
      ValidationRule: selectedAttr.ValidationRule || 'none',
      MaxLength: selectedAttr.MaxLength || '',
      dataSource: selectedAttr.DataSourceType || 'masterTable',
      sqlType: selectedAttr.CustomSqlType || 'query',
      sqlText: selectedAttr.CustomSqlText || '',
      globalizationSource: selectedAttr.GlobalizationSource || 'countries',
      industryForStatic: '', industryStaticOptions: '',
      industryOptionsJson: selectedAttr.IndustryOptionsJson || '{}',
    });
    // Determine target
    if (selectedAttr.PriceMasterColumn) setTarget('priceMaster');
    else if (selectedAttr.SourceType === 'custom_ext' || selectedAttr.ExtColumn) setTarget('custom');
    else if (selectedAttr.ItemMasterColumn && !selectedAttr.SourceTable) setTarget('itemMaster');
    else setTarget('lookup');

    // Load industry label mappings for this attribute
    fetch(`${API_URL}/api/attribute-engine/attributes/${selectedAttr.AttributeId}/industry-mappings`, { headers: hdrs() })
      .then(r => r.json()).then(j => {
        if (j.success) {
          setIndustryMappings(j.data || []);
          const edits = {};
          (j.data || []).forEach(m => { edits[m.IndustryId] = m.CustomLabel || ''; });
          setLabelEdits(edits);
        }
      }).catch(() => {});

    // Load country mandatory codes for this attribute
    fetch(`${API_URL}/api/attribute-engine/attributes/${selectedAttr.AttributeId}/country-mandatory`, { headers: hdrs() })
      .then(r => r.json()).then(j => {
        if (j.success) setMandatoryCodes(j.data || []);
      }).catch(() => {});
  }, [selectedAttr]);

  const addIndustryOption = () => {
    if (!form.industryForStatic || !form.industryStaticOptions) { erpToast.warning('Pick an industry and type at least one option'); return; }
    let map = {}; try { map = JSON.parse(form.industryOptionsJson || '{}'); } catch { /* start fresh */ }
    map[form.industryForStatic] = form.industryStaticOptions.split(',').map(s => s.trim()).filter(Boolean);
    set({ industryOptionsJson: JSON.stringify(map), industryStaticOptions: '' });
  };

  const buildDataSourcePayload = () => {
    if (!DROPDOWN_CONTROLS.has(form.ControlType) || form.dataSource === 'masterTable') return {};
    if (form.dataSource === 'customSql') return { DataSourceType: 'customSql', CustomSqlType: form.sqlType, CustomSqlText: form.sqlText };
    if (form.dataSource === 'industryStatic') return { DataSourceType: 'industryStatic', IndustryOptionsJson: form.industryOptionsJson };
    if (form.dataSource === 'globalization') return { DataSourceType: 'globalization', GlobalizationSource: form.globalizationSource };
    return {};
  };

  const handlePreview = async () => {
    setPreviewing(true); setPreview(null);
    try {
      const r = await fetch(`${API_URL}/api/attribute-engine/datasource/preview`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ dataSourceType: form.dataSource, customSqlType: form.sqlType, customSqlText: form.sqlText, industryOptionsJson: form.industryOptionsJson, industry: form.industryForStatic, globalizationSource: form.globalizationSource }) });
      const j = await r.json();
      if (j.success) setPreview(j.data); else erpToast.error(j.error || 'Preview failed');
    } catch { erpToast.error('Preview failed'); }
    setPreviewing(false);
  };

  const handleSave = async () => {
    if (!form.AttributeId || !form.Label) { erpToast.warning('Attribute ID and Label are required'); return; }
    setSaving(true);
    try {
      const payload = {
        AttributeId: form.AttributeId, Label: form.Label, ControlType: form.ControlType, GroupId: form.GroupId,
        ValidationRule: TEXT_CONTROLS.has(form.ControlType) ? form.ValidationRule : 'none',
        MaxLength: form.MaxLength ? parseInt(form.MaxLength, 10) : null,
        ...buildDataSourcePayload(),
      };
      if (target === 'lookup') { payload.SourceTable = form.SourceTable; payload.ItemMasterColumn = form.ItemMasterColumn; }
      if (target === 'itemMaster') payload.ItemMasterColumn = form.ItemMasterColumn;
      if (target === 'priceMaster') payload.PriceMasterColumn = form.PriceMasterColumn;
      const url = isEditMode ? `${API_URL}/api/attribute-engine/attributes/${form.AttributeId}` : `${API_URL}/api/attribute-engine/attributes`;
      const r = await fetch(url, { method: isEditMode ? 'PUT' : 'POST', headers: hdrs(), body: JSON.stringify(payload) });
      const j = await r.json();
      if (j.success) {
        erpToast.success(j.data?.SourceType === 'custom_ext' ? `"${form.Label}" saved — a new column was created for it` : `Attribute "${form.Label}" saved`);
        if (!isEditMode) { setForm(EMPTY_FORM); setPreview(null); }
        await onSaved();
      } else erpToast.error(j.error || 'Save failed');
    } catch { erpToast.error('Save failed'); }
    setSaving(false);
  };

  // Save all industry label overrides at once
  const saveIndustryLabels = async () => {
    if (!selectedAttr) return;
    setSavingLabels(true);
    try {
      const entries = Object.entries(labelEdits);
      await Promise.all(entries.map(([industryId, customLabel]) =>
        fetch(`${API_URL}/api/attribute-engine/matrix/cell`, {
          method: 'PUT', headers: hdrs(),
          body: JSON.stringify({ industryId, attributeId: selectedAttr.AttributeId, active: true, customLabel }),
        })
      ));
      erpToast.success('Industry labels saved');
    } catch { erpToast.error('Save failed'); }
    setSavingLabels(false);
  };

  // Toggle a country in the mandatory list
  const toggleCountry = (code) => {
    setMandatoryCodes(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const saveCountryMandatory = useCallback(async () => {
    if (!selectedAttr) return;
    setSavingMandatory(true);
    try {
      const r = await fetch(`${API_URL}/api/attribute-engine/attributes/${selectedAttr.AttributeId}/country-mandatory`, {
        method: 'PUT', headers: hdrs(), body: JSON.stringify({ countryCodes: mandatoryCodes }),
      });
      const j = await r.json();
      if (j.success) erpToast.success('Country mandatory settings saved');
      else erpToast.error(j.error || 'Save failed');
    } catch { erpToast.error('Save failed'); }
    setSavingMandatory(false);
  }, [selectedAttr, mandatoryCodes]);

  const filteredCountries = countrySearch
    ? COMMON_COUNTRIES.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()) || c.code.toLowerCase().includes(countrySearch.toLowerCase()))
    : COMMON_COUNTRIES;

  return (
    <div className="erp-form-card">
      <div style={{ padding: 'var(--erp-sp-2)', fontWeight: 600, fontSize: 12, color: 'var(--erp-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
        {isEditMode ? (
          <>
            <Tag size={13} />
            Edit: <span style={{ color: 'var(--erp-text)' }}>{selectedAttr.Label}</span>
            <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 10, color: 'var(--erp-text-muted)', marginLeft: 4 }}>({selectedAttr.AttributeId})</span>
          </>
        ) : (
          <><Plus size={13} /> Add Attribute (global — usable by any industry)</>
        )}
      </div>

      <div style={{ padding: 'var(--erp-sp-2)' }}>
        {/* Basic fields */}
        <div className="erp-form-row">
          <label className="erp-form-label required">Attribute ID</label>
          <div className="erp-form-control">
            <input className="erp-input" value={form.AttributeId} onChange={e => set({ AttributeId: e.target.value })}
              placeholder="e.g. season" disabled={isEditMode} data-testid="attr-id-input"
              style={isEditMode ? { background: 'var(--erp-surface-alt)', color: 'var(--erp-text-muted)' } : {}} />
          </div>
        </div>
        <div className="erp-form-row">
          <label className="erp-form-label required">Label</label>
          <div className="erp-form-control">
            <input className="erp-input" value={form.Label} onChange={e => set({ Label: e.target.value })} data-testid="attr-label-input" />
          </div>
        </div>

        <div className="erp-form-row">
          <label className="erp-form-label">Control Type</label>
          <div className="erp-form-control">
            <select className="erp-input erp-select" value={form.ControlType} onChange={e => set({ ControlType: e.target.value })} data-testid="attr-controltype-select">
              {Object.entries(CONTROL_TYPE_GROUPS).map(([grp, types]) =>
                <optgroup key={grp} label={grp}>{types.map(c => <option key={c} value={c}>{c}</option>)}</optgroup>
              )}
            </select>
          </div>
        </div>

        <div className="erp-form-row">
          <label className="erp-form-label">Group</label>
          <div className="erp-form-control">
            <select className="erp-input erp-select" value={form.GroupId} onChange={e => set({ GroupId: e.target.value })} data-testid="attr-group-select">
              <option value="">-- None --</option>
              {groups.map(g => <option key={g.GroupId} value={g.GroupId}>{g.Icon} {g.Label}</option>)}
            </select>
            {form.GroupId && GROUP_LEGEND[form.GroupId] && (
              <div style={{ fontSize: 10, color: 'var(--erp-text-muted)', marginTop: 4, display: 'flex', gap: 4 }}>
                <Info size={11} style={{ flexShrink: 0, marginTop: 1 }} /> {GROUP_LEGEND[form.GroupId]}
              </div>
            )}
          </div>
        </div>

        {TEXT_CONTROLS.has(form.ControlType) && (
          <div className="erp-form-row">
            <label className="erp-form-label">Validation</label>
            <div className="erp-form-control" style={{ display: 'flex', gap: 6 }}>
              <select className="erp-input erp-select" value={form.ValidationRule} onChange={e => set({ ValidationRule: e.target.value })} data-testid="attr-validation-select">
                {VALIDATION_RULES.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
              </select>
              <input className="erp-input" style={{ width: 80 }} type="number" placeholder="Max len" value={form.MaxLength} onChange={e => set({ MaxLength: e.target.value })} data-testid="attr-maxlength-input" />
            </div>
          </div>
        )}

        {/* ── Image / File Attachment Parameters ── */}
        {form.ControlType === 'ImageEdit' && (
          <div style={{ marginTop: 6, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>🖼</span> Image Control Parameters
            </div>
            <div className="erp-form-row">
              <label className="erp-form-label">Accept Types</label>
              <div className="erp-form-control">
                <select className="erp-input erp-select" value={form.imageAccept} onChange={e => set({ imageAccept: e.target.value })} data-testid="attr-img-accept">
                  <option value="image/*">All images (jpg, png, gif, webp…)</option>
                  <option value="image/jpeg,image/jpg">JPEG only</option>
                  <option value="image/png">PNG only</option>
                  <option value="image/jpeg,image/png">JPEG + PNG</option>
                  <option value="image/webp">WebP only</option>
                </select>
              </div>
            </div>
            <div className="erp-form-row">
              <label className="erp-form-label">Max Size (KB)</label>
              <div className="erp-form-control">
                <input className="erp-input" type="number" min="50" max="10240" value={form.imageMaxKb} onChange={e => set({ imageMaxKb: e.target.value })} placeholder="500" data-testid="attr-img-maxkb" />
              </div>
            </div>
            <div className="erp-form-row">
              <label className="erp-form-label">Thumbnail</label>
              <div className="erp-form-control">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                  <input type="checkbox" checked={!!form.imageThumbnail} onChange={e => set({ imageThumbnail: e.target.checked })} />
                  Show inline thumbnail preview
                </label>
              </div>
            </div>
          </div>
        )}

        {form.ControlType === 'FileAttachment' && (
          <div style={{ marginTop: 6, padding: '8px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#0369a1', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>📎</span> File Attachment Parameters
            </div>
            <div className="erp-form-row">
              <label className="erp-form-label">Accept Types</label>
              <div className="erp-form-control">
                <input className="erp-input" value={form.fileAccept} onChange={e => set({ fileAccept: e.target.value })} placeholder="e.g. .pdf,.xlsx,image/*" data-testid="attr-file-accept" />
              </div>
            </div>
            <div className="erp-form-row">
              <label className="erp-form-label">Max Size (KB)</label>
              <div className="erp-form-control">
                <input className="erp-input" type="number" min="100" max="51200" value={form.fileMaxKb} onChange={e => set({ fileMaxKb: e.target.value })} placeholder="2048" data-testid="attr-file-maxkb" />
              </div>
            </div>
          </div>
        )}

        {/* For media controls, target is always 'custom' — auto-set and hide selector */}
        {!['ImageEdit', 'FileAttachment'].includes(form.ControlType) ? (
        <div className="erp-form-row">
          <label className="erp-form-label">Where does this live?</label>
          <div className="erp-form-control">
            <select className="erp-input erp-select" value={target} onChange={e => setTarget(e.target.value)} data-testid="attr-target-select">
              <option value="lookup">Lookup table (Division, Brand, Category...)</option>
              <option value="itemMaster">A real ItemMaster column</option>
              <option value="priceMaster">A real Price_Master column (pricing)</option>
              <option value="custom">Genuinely new — create a real column for it</option>
            </select>
          </div>
        </div>
        ) : (
          <div style={{ fontSize: 10, color: '#0369a1', padding: '5px 8px', background: '#f0f9ff', borderRadius: 4, border: '1px solid #bae6fd', marginTop: 6 }}>
            Media fields are stored as a new dedicated column (auto-configured).
          </div>
        )}
        {target === 'lookup' && <>
          <div className="erp-form-row"><label className="erp-form-label">Source Table</label><div className="erp-form-control"><select className="erp-input erp-select" value={form.SourceTable} onChange={e => set({ SourceTable: e.target.value })} data-testid="attr-sourcetable-select"><option value="">-- Select --</option>{SOURCE_TABLES.map(t => <option key={t} value={t}>{t}</option>)}</select></div></div>
          <div className="erp-form-row"><label className="erp-form-label">ItemMaster Column</label><div className="erp-form-control"><input className="erp-input" value={form.ItemMasterColumn} onChange={e => set({ ItemMasterColumn: e.target.value })} placeholder="e.g. DivUId" data-testid="attr-itemcol-input" /></div></div>
          {form.SourceTable && form.AttributeId && (
            <GetInfoConfigPanel tableName={form.SourceTable} attrId={form.AttributeId} label={form.Label} />
          )}
        </>}
        {target === 'itemMaster' && <div className="erp-form-row"><label className="erp-form-label">ItemMaster Column</label><div className="erp-form-control"><input className="erp-input" value={form.ItemMasterColumn} onChange={e => set({ ItemMasterColumn: e.target.value })} placeholder="e.g. Fit" data-testid="attr-itemcol-input" /></div></div>}
        {target === 'priceMaster' && <div className="erp-form-row"><label className="erp-form-label">Price_Master Column</label><div className="erp-form-control"><select className="erp-input erp-select" value={form.PriceMasterColumn} onChange={e => set({ PriceMasterColumn: e.target.value })} data-testid="attr-pricecol-select"><option value="">-- Select --</option>{PRICE_MASTER_COLUMNS.map(c => <option key={c} value={c}>{c}</option>)}</select></div></div>}
        {target === 'custom' && <div style={{ fontSize: 10, color: 'var(--erp-text-muted)', padding: '6px 4px', lineHeight: 1.5 }}>A dedicated column "<b>{form.AttributeId || 'yourAttributeId'}</b>" will be created on the companion tables.</div>}

        {DROPDOWN_CONTROLS.has(form.ControlType) && target !== 'lookup' && (
          <div style={{ marginTop: 10, borderTop: '1px dashed var(--erp-border)', paddingTop: 10 }}>
            <div className="erp-form-row"><label className="erp-form-label">Data Source</label><div className="erp-form-control">
              <select className="erp-input erp-select" value={form.dataSource} onChange={e => { set({ dataSource: e.target.value }); setPreview(null); }} data-testid="attr-datasource-select">
                <option value="masterTable">Master Table</option>
                <option value="customSql">Custom SQL / View / Proc</option>
                <option value="industryStatic">Industry-specific static list</option>
                <option value="globalization">Globalization (countries/currencies/tax)</option>
              </select>
            </div></div>
            {form.dataSource === 'masterTable' && <div className="erp-form-row"><label className="erp-form-label">Source Table</label><div className="erp-form-control"><select className="erp-input erp-select" value={form.SourceTable} onChange={e => set({ SourceTable: e.target.value })} data-testid="attr-ds-sourcetable-select"><option value="">-- Select --</option>{SOURCE_TABLES.map(t => <option key={t} value={t}>{t}</option>)}</select></div></div>}
            {form.dataSource === 'customSql' && <>
              <div className="erp-form-row"><label className="erp-form-label">Type</label><div className="erp-form-control"><select className="erp-input erp-select" value={form.sqlType} onChange={e => set({ sqlType: e.target.value })} data-testid="attr-sqltype-select"><option value="query">SELECT query</option><option value="view">View</option><option value="function">Table Function</option><option value="proc">Stored Proc</option></select></div></div>
              <div className="erp-form-row"><label className="erp-form-label">{form.sqlType === 'query' ? 'SELECT statement' : 'Object name'}</label><div className="erp-form-control"><textarea className="erp-input" rows={3} value={form.sqlText} onChange={e => set({ sqlText: e.target.value })} placeholder={form.sqlType === 'query' ? 'SELECT Id, Name FROM MyView' : 'dbo.MyView'} data-testid="attr-sqltext-input" /></div></div>
              <button className="erp-btn erp-btn-secondary" onClick={handlePreview} disabled={previewing} data-testid="attr-sql-preview-btn">{previewing ? 'Testing...' : 'Preview / Test'}</button>
            </>}
            {form.dataSource === 'industryStatic' && <>
              <div className="erp-form-row"><label className="erp-form-label">Industry</label><div className="erp-form-control"><select className="erp-input erp-select" value={form.industryForStatic} onChange={e => set({ industryForStatic: e.target.value })} data-testid="attr-static-industry-select"><option value="">-- Select --</option>{industries.map(i => <option key={i.IndustryId} value={i.IndustryId}>{i.IndustryName}</option>)}</select></div></div>
              <div className="erp-form-row"><label className="erp-form-label">Options (comma-separated)</label><div className="erp-form-control"><input className="erp-input" value={form.industryStaticOptions} onChange={e => set({ industryStaticOptions: e.target.value })} placeholder="Wedding, Engagement, Festive" data-testid="attr-static-options-input" /></div></div>
              <button className="erp-btn erp-btn-secondary" onClick={addIndustryOption} data-testid="attr-static-add-btn">+ Add for this industry</button>
              <div style={{ fontSize: 10, color: 'var(--erp-text-muted)', marginTop: 6, fontFamily: 'monospace' }}>{form.industryOptionsJson}</div>
            </>}
            {form.dataSource === 'globalization' && <div className="erp-form-row"><label className="erp-form-label">Source</label><div className="erp-form-control"><select className="erp-input erp-select" value={form.globalizationSource} onChange={e => set({ globalizationSource: e.target.value })} data-testid="attr-globalization-select"><option value="countries">Countries (196)</option><option value="currencies">Currencies</option><option value="taxSystems">Tax Systems</option></select></div></div>}
            {preview && <div className="erp-grid-wrapper" style={{ marginTop: 8, maxHeight: 140, overflow: 'auto' }} data-testid="attr-ds-preview-result"><table className="erp-grid"><tbody>{preview.slice(0, 10).map((r, i) => <tr key={i}>{Object.values(r).map((v, j) => <td key={j} style={{ fontSize: 10 }}>{String(v)}</td>)}</tr>)}</tbody></table></div>}
          </div>
        )}

        <button className="erp-btn erp-btn-primary" style={{ width: '100%', marginTop: 8 }} onClick={handleSave} disabled={saving} data-testid="attr-save-btn">
          {isEditMode ? <><Save size={13} /> {saving ? 'Saving...' : 'Update Attribute'}</> : <><Plus size={13} /> {saving ? 'Saving...' : 'Add to Library'}</>}
        </button>

        {/* ── EDIT MODE EXTRA SECTIONS ── */}
        {isEditMode && (
          <>
            {/* ── Industry Label Overrides ── */}
            {!hideIndustrySection && (
            <div style={{ marginTop: 12, borderTop: '1px solid var(--erp-border)', paddingTop: 8 }}>
              <button
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', width: '100%', padding: '4px 0', fontWeight: 700, fontSize: 11, color: 'var(--erp-primary)' }}
                onClick={() => setShowIndustryLabels(v => !v)}
                data-testid="industry-labels-toggle"
              >
                {showIndustryLabels ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <Tag size={12} /> Industry Label Overrides
                <span style={{ fontWeight: 400, color: 'var(--erp-text-muted)', fontSize: 10, marginLeft: 4 }}>
                  ({industryMappings.length} industries)
                </span>
              </button>
              {showIndustryLabels && (
                <div style={{ marginTop: 8 }} data-testid="industry-labels-section">
                  <div style={{ fontSize: 10, color: 'var(--erp-text-muted)', marginBottom: 8 }}>
                    Override the label for this attribute in specific industries. Leave blank to use the default label "<b>{selectedAttr.Label}</b>".
                  </div>
                  <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid var(--erp-border)', borderRadius: 4 }}>
                    {industryMappings.length === 0 ? (
                      <div style={{ padding: 12, textAlign: 'center', fontSize: 11, color: 'var(--erp-text-muted)' }}>
                        This attribute is not mapped to any industry yet. Add it via the Industry Config matrix first.
                      </div>
                    ) : industryMappings.map(ind => (
                      <div key={ind.IndustryId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderBottom: '1px solid var(--erp-border)' }}>
                        <span style={{ fontSize: 12, width: 18 }}>{ind.Icon}</span>
                        <span style={{ flex: 1, fontSize: 11, fontWeight: 600 }}>{ind.IndustryName}</span>
                        <input
                          className="erp-input"
                          style={{ width: 120, fontSize: 11, padding: '2px 6px' }}
                          placeholder={selectedAttr.Label}
                          value={labelEdits[ind.IndustryId] || ''}
                          onChange={e => setLabelEdits(p => ({ ...p, [ind.IndustryId]: e.target.value }))}
                          data-testid={`industry-label-input-${ind.IndustryId}`}
                        />
                        {labelEdits[ind.IndustryId] && (
                          <button style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--erp-text-muted)', padding: 0 }}
                            onClick={() => setLabelEdits(p => ({ ...p, [ind.IndustryId]: '' }))}><X size={10} /></button>
                        )}
                      </div>
                    ))}
                  </div>
                  {industryMappings.length > 0 && (
                    <button className="erp-btn erp-btn-primary" style={{ width: '100%', marginTop: 8, fontSize: 11 }}
                      onClick={saveIndustryLabels} disabled={savingLabels} data-testid="save-industry-labels-btn">
                      <Save size={11} /> {savingLabels ? 'Saving...' : 'Save Industry Labels'}
                    </button>
                  )}
                </div>
              )}
            </div>
            )}

            {/* ── Country Mandatory ── */}
            <div style={{ marginTop: 8, borderTop: '1px solid var(--erp-border)', paddingTop: 8 }}>
              <button
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', width: '100%', padding: '4px 0', fontWeight: 700, fontSize: 11, color: '#065f46' }}
                onClick={() => setShowCountryMandatory(v => !v)}
                data-testid="country-mandatory-toggle"
              >
                {showCountryMandatory ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <Globe2 size={12} /> Country Mandatory
                {mandatoryCodes.length > 0 && (
                  <span style={{ background: '#065f46', color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 8, fontWeight: 700 }}>
                    {mandatoryCodes.length}
                  </span>
                )}
              </button>
              {showCountryMandatory && (
                <div style={{ marginTop: 8 }} data-testid="country-mandatory-section">
                  <div style={{ fontSize: 10, color: 'var(--erp-text-muted)', marginBottom: 8 }}>
                    Mark countries where this field is <b>required</b> (e.g. GSTIN mandatory in India, TRN in UAE).
                  </div>
                  {/* Company country quick-toggle */}
                  {companyCountry && (() => {
                    const cc = COMMON_COUNTRIES.find(c => c.code === companyCountry);
                    return cc ? (
                      <div style={{ marginBottom: 8, padding: '6px 10px', background: '#f0fdf4', border: '1px solid #16a34a', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11 }}>{cc.flag}</span>
                        <span style={{ flex: 1, fontSize: 11, fontWeight: 700 }}>{cc.name} <span style={{ fontSize: 9, fontWeight: 400, color: '#16a34a' }}>(your company)</span></span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 11 }}>
                          <input type="checkbox" checked={mandatoryCodes.includes(cc.code)} onChange={() => toggleCountry(cc.code)} />
                          Required
                        </label>
                      </div>
                    ) : null;
                  })()}
                  <input className="erp-input" style={{ marginBottom: 8, fontSize: 11 }}
                    placeholder="Search other countries…" value={countrySearch}
                    onChange={e => setCountrySearch(e.target.value)}
                    data-testid="country-search-input" />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4, maxHeight: 180, overflow: 'auto' }}>
                    {filteredCountries.filter(c => c.code !== companyCountry).map(c => (
                      <label key={c.code} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', cursor: 'pointer', borderRadius: 4, background: mandatoryCodes.includes(c.code) ? '#f0fdf4' : 'transparent', border: `1px solid ${mandatoryCodes.includes(c.code) ? '#16a34a' : 'var(--erp-border)'}`, fontSize: 11 }}
                        data-testid={`country-check-${c.code}`}>
                        <input type="checkbox" checked={mandatoryCodes.includes(c.code)} onChange={() => toggleCountry(c.code)} style={{ flexShrink: 0 }} />
                        <span>{c.flag}</span>
                        <span style={{ fontWeight: mandatoryCodes.includes(c.code) ? 700 : 400 }}>{c.name}</span>
                      </label>
                    ))}
                  </div>
                  <button className="erp-btn erp-btn-primary" style={{ width: '100%', marginTop: 8, fontSize: 11, background: '#065f46', borderColor: '#065f46' }}
                    onClick={saveCountryMandatory} disabled={savingMandatory} data-testid="save-country-mandatory-btn">
                    <Globe2 size={11} /> {savingMandatory ? 'Saving...' : `Save Mandatory (${mandatoryCodes.length} countries)`}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
